import { useForm } from '@mantine/form';
import {
  ActionIcon,
  Button,
  Group,
  Stack,
  TagsInput,
  Text,
  TextInput,
} from '@mantine/core';
import React, { useEffect, useRef, useState } from 'react';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { getConnectorTypeLabel } from '../../../const/charging_connector_type_mapping.ts';
import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import { useQueryClient } from '@tanstack/react-query';
import useCreateEVSE from '../../../hooks/evses/useCreateEVSE.ts';
import { CreateEVSERequest } from '../../../api/requests/evse-request.ts';
import { getEVSE } from '../../../api/evse.ts';
import useEVSE from '../../../hooks/evses/useEVSE.ts';
import { ChargingConnector } from '../../../models/ChargingConnector.ts';
import useRemoveChargingConnector from '../../../hooks/charging-connectors/useRemoveChargingConnector.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { RemoveChargingConnectorRequest } from '../../../api/requests/charging-connector-request.ts';
import { useDisclosure } from '@mantine/hooks';
import { AddChargingConnectorModal } from '../../modal/places/AddChargingConnectorModal.tsx';
import { modals } from '@mantine/modals';
import { UpdateChargingConnectorModal } from '../../modal/places/UpdateChargingConnectorModal.tsx';

export interface MutateEVSEAndConnectorsFormValues {
  serviceId: string | null;
  isEVSEAlreadyCreatedBefore: boolean;
  isEVSENewlyCreatedDuringThisProcess: boolean;
  evseId: string | null;
  evseSerialNumber: string;
  evseFloorLevel: string[];
  chargingConnectors: ChargingConnector[];
}

interface MutateEVSEAndConnectorsFormProps {
  initialValues: MutateEVSEAndConnectorsFormValues;
  onSubmit: (formValues: MutateEVSEAndConnectorsFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  onEvseCreated?: (evseId: string) => void;
}

export default function MutateEVSEAndConnectorsForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  onEvseCreated,
}: MutateEVSEAndConnectorsFormProps) {
  const form = useForm<MutateEVSEAndConnectorsFormValues>({
    mode: 'controlled',
    initialValues: {
      serviceId: initialValues.serviceId,
      isEVSEAlreadyCreatedBefore: initialValues.isEVSEAlreadyCreatedBefore,
      isEVSENewlyCreatedDuringThisProcess:
        initialValues.isEVSENewlyCreatedDuringThisProcess,
      evseId: initialValues.evseId,
      evseSerialNumber: initialValues.evseSerialNumber,
      evseFloorLevel: initialValues.evseFloorLevel,
      chargingConnectors: initialValues.chargingConnectors,
    },
  });

  const [
    addChargingConnectorModalOpened,
    {
      open: openAddChargingConnectorModal,
      close: closeAddChargingConnectorModal,
    },
  ] = useDisclosure(false);

  const [
    updateChargingConnectorModalOpened,
    {
      open: openUpdateChargingConnectorModal,
      close: closeUpdateChargingConnectorModal,
    },
  ] = useDisclosure(false);

  const [tempEvseId, setTempEvseId] = useState<string | null>(null);
  const [currentTargetConnector, setCurrentTargetConnector] = useState<
    string | null
  >(null);

  const { setFieldValue, values } = form;

  const { mutateAsync: createEVSE, isSuccess } = useCreateEVSE();

  const localEvseId = initialValues.isEVSEAlreadyCreatedBefore
    ? initialValues.evseId
    : form.values.evseId;

  const localIsEVSECreated =
    initialValues.isEVSENewlyCreatedDuringThisProcess || isSuccess;
  const { mutateAsync: removeChargingConnector } = useRemoveChargingConnector();
  const { data: evse } = useEVSE(localEvseId);

  const queryClient = useQueryClient();

  // 用 ref 持有最新的 values / setFieldValue，讓 effect 能讀取或呼叫最新版本而不成為
  // dependency，避免 Mantine controlled mode 每次 form state 更新就產生新 reference
  // 而觸發 effect 無限迴圈。
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const setFieldValueRef = useRef(setFieldValue);
  setFieldValueRef.current = setFieldValue;

  // 同步 evse 數據到表單
  useEffect(() => {
    if (evse?.connectors) {
      setFieldValueRef.current(
        'chargingConnectors',
        Object.values(evse.connectors),
      );
    }

    if (
      valuesRef.current.evseSerialNumber == null &&
      evse?.evseSerialNumber != null
    ) {
      setFieldValueRef.current('evseSerialNumber', evse.evseSerialNumber);
    }

    if (
      !valuesRef.current.evseFloorLevel?.length &&
      evse?.floorLevels != null
    ) {
      setFieldValueRef.current('evseFloorLevel', evse.floorLevels);
    }
  }, [evse, localEvseId]);

  const handleChargingConnectorAdded = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['evses', tempEvseId],
      exact: true,
    });

    await queryClient.invalidateQueries({
      queryKey: ['chargingServices', initialValues.serviceId, 'evses'],
      exact: true,
    });

    await queryClient.refetchQueries({
      queryKey: ['evses', tempEvseId],
      exact: true,
    });

    closeAddChargingConnectorModal();
  };

  const handleAddChargingConnectorClick = async () => {
    let evseId = localEvseId;

    // 如果還沒創建 EVSE,先創建
    if (!initialValues.isEVSEAlreadyCreatedBefore && !localIsEVSECreated) {
      form.setFieldValue('evseFloorLevel', form.values.evseFloorLevel);
      form.setFieldValue('evseSerialNumber', form.values.evseSerialNumber);

      const createEVSERequest: CreateEVSERequest = {
        chargingServiceId: form.values.serviceId,
        floorLevels: form.values.evseFloorLevel,
        evseSerialNumber: form.values.evseSerialNumber,
        status: 'OPERATIONAL',
      };

      console.log(createEVSERequest);

      const result = await createEVSE(createEVSERequest);
      evseId = result.id;

      // 同步更新表單
      form.setFieldValue('evseId', result.id);
      form.setFieldValue('isEVSENewlyCreatedDuringThisProcess', true);

      // 通知父組件
      onEvseCreated?.(result.id);
    }

    // 確保有最新的 EVSE 數據
    await queryClient.fetchQuery({
      queryKey: ['evses', evseId],
      queryFn: () => getEVSE(evseId),
    });

    setTempEvseId(evseId);
    openAddChargingConnectorModal();
  };

  const handleChargingConnectorUpdated = async () => {
    setCurrentTargetConnector(null);

    await queryClient.invalidateQueries({
      queryKey: ['evses', tempEvseId],
      exact: true,
    });

    await queryClient.invalidateQueries({
      queryKey: ['chargingServices', initialValues.serviceId, 'evses'],
      exact: true,
    });

    await queryClient.refetchQueries({
      queryKey: ['evses', tempEvseId],
      exact: true,
    });

    closeAddChargingConnectorModal();
  };

  const handleEditChargingConnectorClick = async (connectorId: string) => {
    setCurrentTargetConnector(connectorId);
    openUpdateChargingConnectorModal();
  };

  const handleRemoveChargingConnectorClick = async (
    evseId: string,
    connectorId: string,
  ) => {
    try {
      const removeChargingConnectorRequest: RemoveChargingConnectorRequest = {
        evseId: evseId,
        connectorId: connectorId,
      };
      await removeChargingConnector(removeChargingConnectorRequest);
      notifySuccess('刪除充電槍成功');

      await queryClient.invalidateQueries({
        queryKey: ['evses', evseId],
        exact: true,
      });
      await queryClient.invalidateQueries({
        queryKey: ['chargingServices', initialValues.serviceId, 'evses'],
        exact: true,
      });
    } catch (error) {
      notifyError('刪除充電槍失敗', error.message);
    }
  };

  const handleSubmit = form.onSubmit(
    (formValues: MutateEVSEAndConnectorsFormValues) => {
      onSubmit({
        ...formValues,
        evseFloorLevel: formValues.evseFloorLevel,
        evseId: localEvseId,
        isEVSEAlreadyCreatedBefore: localIsEVSECreated,
      });
    },
  );

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const firstConnector = evse?.connectors
    ? Object.values(evse.connectors)[0]
    : null;
  const secondConnector = evse?.connectors
    ? Object.values(evse.connectors)[1]
    : null;

  const transformFloorLevel = (value: string): string => {
    if (!/^-?\d+(\.5)?$/.test(value)) return value; // 只接受整數或 .5

    const num = parseFloat(value);
    const isMezzanine = value.endsWith('.5');
    const base = Math.floor(Math.abs(num));

    if (isMezzanine) {
      return num >= 0 ? `${base}M` : `B${base}M`;
    }

    return num >= 0 ? `${num}F` : `B${Math.abs(num)}`;
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Text
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#ADB5BD',
            paddingBottom: 10,
            borderBottom: '1px solid #DEE2E6',
          }}
        >
          充電樁設定
        </Text>

        <Group gap={16} style={{ paddingTop: 16, paddingBottom: 24 }}>
          {/* 充電樁編號 */}
          <TextInput
            label={'充電樁編號'}
            placeholder={'請輸入充電樁編號'}
            key={form.key('evseSerialNumber')}
            {...form.getInputProps('evseSerialNumber')}
            styles={{
              input: {
                width: 305.5,
                borderColor: form.errors.name
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
          {/* 充電樁樓層 */}
          <TagsInput
            label={'充電樁樓層'}
            placeholder={'請輸入充電樁樓層'}
            key={form.key('evseFloorLevel')}
            {...form.getInputProps('evseFloorLevel')}
            onChange={(values) => {
              const transformed = values.map(transformFloorLevel);
              form.setFieldValue('evseFloorLevel', transformed);
            }}
            allowDuplicates={false}
            styles={{
              wrapper: {
                width: 305.5,
                flexWrap: 'nowrap',
                overflowX: 'auto',
              },
              input: {
                borderColor: form.errors.evseFloorLevel
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
              pillsList: {
                flexWrap: 'nowrap',
                overflowX: 'auto',
                scrollbarWidth: 'thin',
              },
            }}
          />
        </Group>

        <Text
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#ADB5BD',
            paddingBottom: 10,
            borderBottom: '1px solid #DEE2E6',
          }}
        >
          充電槍設定
        </Text>

        <Group gap={13} style={{ paddingTop: 16 }}>
          {firstConnector ? (
            <div
              style={{
                width: 307,
                height: 168,
                backgroundColor: '#F8F9FA',
                borderRadius: 4,
                padding: 16,
              }}
            >
              <div style={{ width: 275, height: 40, marginBottom: 16 }}>
                <Group justify={'space-between'}>
                  <Stack gap={0}>
                    <Text
                      style={{
                        lineHeight: '20px',
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      {Object.values(evse.connectors)[0]
                        .connectorSerialNumber !== ''
                        ? Object.values(evse.connectors)[0]
                            .connectorSerialNumber
                        : '-'}
                    </Text>
                    <Text
                      style={{
                        lineHeight: '20px',
                        fontSize: 12,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      {getConnectorTypeLabel(
                        Object.values(evse.connectors)[0].connectorType,
                      )}
                      {' · '}
                      {Object.values(evse.connectors)[0].maxPower.value}
                      {Object.values(evse.connectors)[0].maxPower.unit}
                      {' · '}
                      {Object.values(evse.connectors)[0].powerType}
                    </Text>
                  </Stack>
                  <Group gap={12}>
                    <ActionIcon
                      size={18}
                      variant={'light'}
                      color={'red'}
                      onClick={() => {
                        modals.openConfirmModal({
                          title: (
                            <Text style={{ fontSize: 16, fontWeight: 700 }}>
                              刪除充電槍
                            </Text>
                          ),
                          children: (
                            <Text style={{ fontSize: 14, fontWeight: 400 }}>
                              是否確認刪除充電槍？
                            </Text>
                          ),
                          centered: true,
                          labels: { confirm: '確認', cancel: '取消' },
                          confirmProps: { color: 'blue' },

                          onConfirm: async () => {
                            await handleRemoveChargingConnectorClick(
                              form.values.evseId,
                              Object.values(evse.connectors)[0].id,
                            );
                          },
                        });
                      }}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                    <ActionIcon
                      size={18}
                      variant={'light'}
                      color={'blue'}
                      onClick={() =>
                        handleEditChargingConnectorClick(
                          Object.values(evse.connectors)[0].id,
                        )
                      }
                    >
                      <IconEdit size={18} />
                    </ActionIcon>
                  </Group>
                </Group>
              </div>

              <Stack gap={0}>
                <Text style={{ fontSize: 12, fontWeight: 400 }}>
                  充電費率：
                </Text>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    maxHeight: 56,
                    maxWidth: 275,
                    wordWrap: 'break-word',
                    overflowY: 'auto',
                  }}
                >
                  {Object.values(evse.connectors)[0].chargingRate.rateText
                    .length !== 0
                    ? Object.values(evse.connectors)[0].chargingRate.rateText
                    : '-'}
                </Text>
              </Stack>
            </div>
          ) : (
            <Button
              variant={'filled'}
              color={'#F8F9FA'}
              onClick={handleAddChargingConnectorClick}
              style={{
                width: 307,
                height: 168,
                borderRadius: 4,
                alignContent: 'center',
              }}
            >
              <Group justify={'center'}>
                <IconPlus color={'#228BE6'} size={16} />
                <Text
                  style={{ color: '#228BE6', fontSize: 14, fontWeight: 400 }}
                >
                  新增充電槍
                </Text>
              </Group>
            </Button>
          )}

          {secondConnector ? (
            <div
              style={{
                width: 307,
                height: 168,
                backgroundColor: '#F8F9FA',
                borderRadius: 4,
                padding: 16,
              }}
            >
              <div style={{ width: 275, height: 40, marginBottom: 16 }}>
                <Group justify={'space-between'}>
                  <Stack gap={0}>
                    <Text
                      style={{
                        lineHeight: '20px',
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      {Object.values(evse.connectors)[1]
                        .connectorSerialNumber !== ''
                        ? Object.values(evse.connectors)[1]
                            .connectorSerialNumber
                        : '-'}
                    </Text>
                    <Text
                      style={{
                        lineHeight: '20px',
                        fontSize: 12,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      {getConnectorTypeLabel(
                        Object.values(evse.connectors)[1].connectorType,
                      )}
                      {' · '}
                      {Object.values(evse.connectors)[1].maxPower.value}
                      {Object.values(evse.connectors)[1].maxPower.unit}
                      {' · '}
                      {Object.values(evse.connectors)[1].powerType}
                    </Text>
                  </Stack>
                  <Group gap={12}>
                    <ActionIcon
                      size={18}
                      variant={'light'}
                      color={'red'}
                      onClick={() => {
                        modals.openConfirmModal({
                          title: (
                            <Text style={{ fontSize: 16, fontWeight: 700 }}>
                              刪除充電槍
                            </Text>
                          ),
                          children: (
                            <Text style={{ fontSize: 14, fontWeight: 400 }}>
                              是否確認刪除充電槍？
                            </Text>
                          ),
                          centered: true,
                          labels: { confirm: '確認', cancel: '取消' },
                          confirmProps: { color: 'blue' },

                          onConfirm: async () => {
                            await handleRemoveChargingConnectorClick(
                              form.values.evseId,
                              Object.values(evse.connectors)[1].id,
                            );
                          },
                        });
                      }}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                    <ActionIcon
                      size={18}
                      variant={'light'}
                      color={'blue'}
                      onClick={() =>
                        handleEditChargingConnectorClick(
                          Object.values(evse.connectors)[1].id,
                        )
                      }
                    >
                      <IconEdit size={18} />
                    </ActionIcon>
                  </Group>
                </Group>
              </div>

              <Stack gap={0}>
                <Text style={{ fontSize: 12, fontWeight: 400 }}>
                  充電費率：
                </Text>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    maxHeight: 56,
                    maxWidth: 275,
                    wordWrap: 'break-word',
                    overflowY: 'auto',
                  }}
                >
                  {Object.values(evse.connectors)[1].chargingRate.rateText
                    .length !== 0
                    ? Object.values(evse.connectors)[1].chargingRate.rateText
                    : '-'}
                </Text>
              </Stack>
            </div>
          ) : (
            <Button
              variant={'filled'}
              color={'#F8F9FA'}
              onClick={handleAddChargingConnectorClick}
              style={{
                width: 307,
                height: 168,
                borderRadius: 4,
                alignContent: 'center',
              }}
            >
              <Group justify={'center'}>
                <IconPlus color={'#228BE6'} size={16} />
                <Text
                  style={{ color: '#228BE6', fontSize: 14, fontWeight: 400 }}
                >
                  新增充電槍
                </Text>
              </Group>
            </Button>
          )}
        </Group>

        {/* 取消 與 新增 or 更新按鈕 */}
        <Group justify="flex-end" mt="24px">
          <Button variant="outline" onClick={handleCancel}>
            關閉
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            儲存
          </Button>
        </Group>
      </form>

      <AddChargingConnectorModal
        opened={addChargingConnectorModalOpened}
        onClose={closeAddChargingConnectorModal}
        evseId={tempEvseId}
        onSuccess={handleChargingConnectorAdded}
        serviceId={form.values.serviceId}
      />

      {currentTargetConnector && (
        <UpdateChargingConnectorModal
          opened={updateChargingConnectorModalOpened}
          onClose={closeUpdateChargingConnectorModal}
          serviceId={form.values.serviceId}
          evseId={form.values.evseId}
          connectorId={currentTargetConnector}
          onSuccess={handleChargingConnectorUpdated}
        />
      )}
    </>
  );
}
